import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc, collection, deleteDoc, doc, limitToLast, onSnapshot, orderBy, query, serverTimestamp, Timestamp,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { app, db, reportMessageFn, requestVerifyLinkFn } from "../firebase";
import { MessagesMode } from "../types";

/**
 * Tapahtuman viestiketju (docs/ideat.md G1).
 *
 * Lyhyet viestit yhden tapahtuman ympärillä: pikanapit (myöhästyn, tuon
 * pallot, paikka vapaana) ja enintään 300 merkin teksti. Ilmoitus menee
 * tapahtuman osallistujille ja kutsutuille (onMessageCreated), ja viestit
 * poistuvat viikon kuluttua tapahtumasta (cleanupExpiredMessages).
 *
 * Kuka kirjoittaa, riippuu tapahtuman asetuksesta (event.messages):
 *  - "verified" (oletus): varmennettu kirjautuminen (kutsulinkki tai
 *    vahvistuslinkki) ja ylläpitäjä. Muille näytetään "Lähetä minulle
 *    vahvistuslinkki" -nappi.
 *  - "all": myös PIN-kirjautuneet; heidän viestinsä merkitään
 *    vahvistamattomiksi, koska PIN-istunnossa nimi on itse kirjoitettu.
 *  - "off": osio on piilossa.
 * Säännöt tarkistavat saman (firestore.rules, canPostMessage).
 *
 * Applen ohje 1.2: asiaton viesti voidaan ilmoittaa, kirjoittajan voi estää
 * omasta näkymästä, ja järjestäjä tai ylläpitäjä voi poistaa minkä tahansa.
 */

interface Message {
  id: string;
  authorId: string;
  authorName: string;
  authorUid?: string;
  unverified?: boolean;
  text: string;
  quick?: "late" | "balls" | "spot";
  createdAt?: Timestamp | null;
}

const MAX_LEN = 300;
const BLOCK_KEY = "spinloop_blocked_authors";

const QUICK: { key: "late" | "balls" | "spot"; fi: string; en: string; icon: string }[] = [
  { key: "late",  icon: "⏰", fi: "Myöhästyn hieman",     en: "Running a bit late" },
  { key: "balls", icon: "🎒", fi: "Tuon pallot",           en: "I'll bring balls" },
  { key: "spot",  icon: "🙋", fi: "Yksi paikka vapaana",   en: "One spot open" },
];

function readBlocked(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(BLOCK_KEY) || "[]")); }
  catch { return new Set(); }
}
function writeBlocked(s: Set<string>) {
  try { localStorage.setItem(BLOCK_KEY, JSON.stringify(Array.from(s))); } catch { /* ei tallennusta */ }
}

export default function EventMessages({ clubId, eventId, role, myPlayerId, myName, clubName, lang, dark = false, mode = "verified" }: {
  mode?: MessagesMode;
  clubId: string;
  eventId: string;
  role?: string;
  myPlayerId?: string;
  myName?: string;
  clubName?: string;
  lang: "fi" | "en";
  dark?: boolean;
}) {
  const fi = lang !== "en";
  const isAdmin = role === "club_admin";
  const canModerate = isAdmin || role === "organizer";
  const [messages, setMessages] = useState<Message[]>([]);
  const [verified, setVerified] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [blocked, setBlocked] = useState<Set<string>>(readBlocked);
  const [error, setError] = useState("");
  const [verifyMsg, setVerifyMsg] = useState("");
  const [verifyBusy, setVerifyBusy] = useState(false);
  const myUid = getAuth(app).currentUser?.uid ?? "";

  // Kirjoitusoikeus tokenin claimeista, ei sessiosta: sama tieto jota
  // säännöt käyttävät, joten nappi ei lupaa mitä sääntö ei salli.
  useEffect(() => {
    if (isAdmin) { setVerified(true); return; }
    const user = getAuth(app).currentUser;
    if (!user) return;
    user.getIdTokenResult()
      .then(t => setVerified(t.claims.verified === true && !!myPlayerId && t.claims.playerId === myPlayerId))
      .catch(() => setVerified(false));
  }, [isAdmin, myPlayerId]);

  useEffect(() => {
    const q = query(
      collection(db, "clubs", clubId, "events", eventId, "messages"),
      orderBy("createdAt"), limitToLast(100),
    );
    return onSnapshot(q,
      snap => setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() } as Message))),
      err => console.error("EventMessages", err));
  }, [clubId, eventId]);

  const author: { id: string; name: string; unverified?: boolean } | null = isAdmin
    ? { id: "admin", name: (clubName ? `${clubName} (${fi ? "ylläpito" : "admin"})` : (fi ? "Ylläpito" : "Admin")).slice(0, 60) }
    : verified && myPlayerId && myName
      ? { id: myPlayerId, name: myName.slice(0, 60) }
      : mode === "all" && myName
        ? { id: myPlayerId || `name:${myName.trim().toLowerCase()}`, name: myName.slice(0, 60), unverified: true }
        : null;

  async function requestVerify() {
    if (!myPlayerId || verifyBusy) return;
    setVerifyBusy(true);
    setVerifyMsg("");
    try {
      const res = await requestVerifyLinkFn({ playerId: myPlayerId });
      const r = res.data;
      setVerifyMsg(
        r.status === "sent"
          ? (fi ? `Linkki lähetetty osoitteeseen ${r.to}. Avaa se tällä laitteella.` : `Link sent to ${r.to}. Open it on this device.`)
          : r.status === "throttled"
            ? (fi ? `Linkki on jo lähetetty osoitteeseen ${r.to}. Uuden voit pyytää ${r.waitMinutes} minuutin kuluttua.` : `A link was already sent to ${r.to}. You can request a new one in ${r.waitMinutes} min.`)
            : r.status === "no-email"
              ? (fi ? "Profiilissasi ei ole sähköpostia. Pyydä ylläpitäjää lisäämään se, niin voit vahvistaa kirjautumisesi." : "Your profile has no email. Ask the admin to add it so you can verify.")
              : (fi ? "Kirjautumisesi on jo vahvistettu. Lataa sivu uudelleen." : "You're already verified. Reload the page."));
    } catch (err) {
      console.error("EventMessages: vahvistuslinkki", err);
      setVerifyMsg(fi ? "Linkkiä ei voitu lähettää. Yritä hetken päästä uudelleen." : "Could not send the link. Try again shortly.");
    }
    setVerifyBusy(false);
  }

  const visible = useMemo(() => messages.filter(m => !blocked.has(m.authorId)), [messages, blocked]);

  async function send(body: string, quick?: "late" | "balls" | "spot") {
    const t = body.trim().slice(0, MAX_LEN);
    if (!author || !t || sending) return;
    setSending(true);
    setError("");
    try {
      await addDoc(collection(db, "clubs", clubId, "events", eventId, "messages"), {
        authorId: author.id,
        authorName: author.name,
        authorUid: myUid,
        ...(author.unverified ? { unverified: true } : {}),
        text: t,
        ...(quick ? { quick } : {}),
        createdAt: serverTimestamp(),
      });
      if (!quick) setText("");
    } catch (err) {
      console.error("EventMessages: lähetys", err);
      setError(fi ? "Viestiä ei voitu lähettää. Tarkista yhteys." : "Could not send. Check your connection.");
    }
    setSending(false);
  }

  async function remove(m: Message) {
    if (!window.confirm(fi ? "Poistetaanko viesti?" : "Delete this message?")) return;
    try { await deleteDoc(doc(db, "clubs", clubId, "events", eventId, "messages", m.id)); }
    catch (err) { console.error("EventMessages: poisto", err); }
  }

  async function report(m: Message) {
    if (!window.confirm(fi
      ? `Ilmoitetaanko ${m.authorName}n viesti asiattomaksi? Klubin ylläpitäjä saa ilmoituksen.`
      : `Report ${m.authorName}'s message as inappropriate? The club admin is notified.`)) return;
    try {
      await reportMessageFn({ clubId, eventId, messageId: m.id });
      window.alert(fi ? "Kiitos, ilmoitus on lähetetty." : "Thanks, the report was sent.");
    } catch (err) {
      console.error("EventMessages: ilmoitus", err);
      window.alert(fi ? "Ilmoitusta ei voitu lähettää." : "Could not send the report.");
    }
  }

  function block(m: Message) {
    if (!window.confirm(fi
      ? `Piilotetaanko ${m.authorName}n viestit tästä laitteesta?`
      : `Hide ${m.authorName}'s messages on this device?`)) return;
    const next = new Set(blocked); next.add(m.authorId);
    setBlocked(next); writeBlocked(next);
  }

  const text1 = dark ? "#ffffff" : "var(--sl-text)";
  const muted = dark ? "rgba(255,255,255,0.55)" : "var(--sl-muted)";
  const border = dark ? "rgba(255,255,255,0.12)" : "var(--sl-border)";
  const linkBtn: React.CSSProperties = { background: "none", border: "none", padding: 0, fontSize: 11, color: muted, cursor: "pointer" };

  if (mode === "off") return null;

  const timeOf = (m: Message) => {
    const d = m.createdAt?.toDate?.();
    if (!d) return "";
    const today = new Date().toDateString() === d.toDateString();
    return d.toLocaleString(fi ? "fi-FI" : "en-GB", today
      ? { hour: "2-digit", minute: "2-digit" }
      : { day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div style={{
      marginTop: 16, padding: "14px 16px", borderRadius: 14,
      background: dark ? "rgba(255,255,255,0.05)" : "var(--sl-card)", border: `1px solid ${border}`,
    }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: text1, marginBottom: 10 }}>
        💬 {fi ? "Viestit" : "Messages"}
      </div>

      {visible.length === 0 && (
        <div style={{ fontSize: 12, color: muted, marginBottom: 10 }}>
          {fi ? "Ei vielä viestejä. Viestit poistuvat viikon kuluttua tapahtumasta." : "No messages yet. Messages are removed a week after the event."}
        </div>
      )}

      {visible.map(m => {
        const own = (!!myUid && m.authorUid === myUid) || (!!author && !author.unverified && m.authorId === author.id);
        return (
          <div key={m.id} style={{ padding: "8px 0", borderBottom: `1px solid ${border}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: m.authorId === "admin" ? "var(--sl-primary)" : text1 }}>
                {m.authorName}
                {m.unverified && (
                  <span style={{ fontSize: 10, fontWeight: 600, color: muted, marginLeft: 6 }}>
                    {fi ? "vahvistamaton" : "unverified"}
                  </span>
                )}
              </span>
              <span style={{ fontSize: 11, color: muted, flexShrink: 0 }}>{timeOf(m)}</span>
            </div>
            <div style={{ fontSize: 13, color: text1, marginTop: 2, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
              {m.text}
            </div>
            <div style={{ display: "flex", gap: 12, marginTop: 4 }}>
              {(own || canModerate) && (
                <button onClick={() => remove(m)} style={linkBtn}>{fi ? "Poista" : "Delete"}</button>
              )}
              {!own && (
                <>
                  <button onClick={() => report(m)} style={linkBtn}>{fi ? "Ilmoita" : "Report"}</button>
                  {m.authorId !== "admin" && (
                    <button onClick={() => block(m)} style={linkBtn}>{fi ? "Estä" : "Block"}</button>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}

      {author ? (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 12 }}>
            {QUICK.map(q => (
              <button key={q.key} onClick={() => send(fi ? q.fi : q.en, q.key)} disabled={sending} style={{
                border: `1px solid ${border}`, background: "transparent", color: text1,
                borderRadius: 999, padding: "6px 10px", fontSize: 12, cursor: sending ? "wait" : "pointer",
              }}>
                {q.icon} {fi ? q.fi : q.en}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <input
              value={text}
              onChange={e => setText(e.target.value.slice(0, MAX_LEN))}
              onKeyDown={e => { if (e.key === "Enter") send(text); }}
              placeholder={fi ? "Kirjoita lyhyt viesti…" : "Write a short message…"}
              maxLength={MAX_LEN}
              aria-label={fi ? "Viesti" : "Message"}
              style={{
                flex: 1, minWidth: 0, padding: "10px 12px", borderRadius: 10, fontSize: 14,
                border: `1px solid ${border}`, background: dark ? "rgba(255,255,255,0.06)" : "#fff", color: text1,
              }}
            />
            <button onClick={() => send(text)} disabled={sending || !text.trim()} style={{
              border: "none", borderRadius: 10, padding: "0 14px", fontSize: 13, fontWeight: 700,
              background: "var(--sl-primary)", color: "#fff",
              opacity: sending || !text.trim() ? 0.5 : 1, cursor: sending ? "wait" : "pointer",
            }}>
              {fi ? "Lähetä" : "Send"}
            </button>
          </div>
          {text.length > MAX_LEN - 40 && (
            <div style={{ fontSize: 11, color: muted, marginTop: 4 }}>{text.length}/{MAX_LEN}</div>
          )}
          {error && <div style={{ fontSize: 12, color: "var(--sl-error, #dc2626)", marginTop: 6 }}>{error}</div>}
          {author.unverified && myPlayerId && (
            <div style={{ marginTop: 8 }}>
              <button onClick={requestVerify} disabled={verifyBusy} style={linkBtn}>
                {fi ? "Viesteissäsi lukee \"vahvistamaton\". Vahvista nimesi sähköpostilla →" : "Your messages show \"unverified\". Verify your name by email →"}
              </button>
              {verifyMsg && <div style={{ fontSize: 12, color: text1, marginTop: 6, lineHeight: 1.5 }}>{verifyMsg}</div>}
            </div>
          )}
        </>
      ) : (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12, color: muted, lineHeight: 1.5 }}>
            {fi
              ? "Kirjoittaminen vaatii vahvistetun kirjautumisen. Lukea voit jo nyt."
              : "Writing requires a verified sign-in. You can already read."}
          </div>
          {myPlayerId ? (
            <>
              <button onClick={requestVerify} disabled={verifyBusy} style={{
                marginTop: 10, border: "none", borderRadius: 10, padding: "10px 14px", fontSize: 13, fontWeight: 700,
                background: "var(--sl-primary)", color: "#fff", cursor: verifyBusy ? "wait" : "pointer", opacity: verifyBusy ? 0.6 : 1,
              }}>
                📧 {fi ? "Lähetä minulle vahvistuslinkki" : "Send me a verification link"}
              </button>
              {verifyMsg && <div style={{ fontSize: 12, color: text1, marginTop: 8, lineHeight: 1.5 }}>{verifyMsg}</div>}
            </>
          ) : (
            <div style={{ fontSize: 12, color: muted, marginTop: 6 }}>
              {fi ? "Pyydä ylläpitäjältä kutsulinkki." : "Ask the admin for an invite link."}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
